import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { authKeys } from '~/api/auth';
import { GLOBAL_CODES, isApiError } from '~/api/client';
import { notify } from './notify';

/*
 * The global error handler: every query and mutation fails through here first.
 *
 * It takes the failures that have nothing to do with the screen — offline,
 * rate limits, server trouble (one toast per kind, however many calls failed)
 * and an ended session (back to sign in, then here again). It marks those
 * errors `handled`, and screens skip a handled error.
 *
 * Everything else (validation, conflict, not found, forbidden, not configured)
 * is the screen's: it reaches the component through the query or mutation
 * hook — `ErrorNotice` for a query, `formError()` for a form, `notify.error`
 * for a one-click action.
 */

let goToSignIn: () => Promise<unknown> = async () => window.location.assign('/login');

/** The router says how to reach sign in (src/router.tsx); kept here to avoid an import cycle. */
export function onSessionEnded(navigate: () => Promise<unknown>): void {
  goToSignIn = navigate;
}

let ending = false;

function endSession(): void {
  if (ending) return;
  ending = true;
  // Without the cached person, /login asks the server again instead of bouncing back.
  queryClient.removeQueries({ queryKey: authKeys.me, exact: true });
  notify.warning('Your session ended.', 'Sign in again to carry on where you were.', {
    id: 'session-ended',
  });
  void goToSignIn().finally(() => {
    queryClient.clear();
    ending = false;
  });
}

/** True when the global handler took the error; the screen should say nothing more. */
function handleGlobally(error: unknown): boolean {
  if (!isApiError(error) || error.handled || !GLOBAL_CODES.has(error.code)) return false;
  if (error.code === 'unauthenticated') {
    // Not signed in to begin with (the sign-in screens): a wrong password, theirs to show.
    if (!ending && queryClient.getQueryData(authKeys.me) === undefined) return false;
    endSession();
  } else {
    notify.error(error.message, undefined, { id: error.code });
  }
  error.handled = true;
  return true;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isApiError(error) && error.code === 'unauthenticated') {
        handleGlobally(error);
        return;
      }
      // A first load shows ErrorNotice in place of the content. A background
      // refresh keeps the last data on screen, so only a toast can say it failed.
      if (query.state.data !== undefined) handleGlobally(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      handleGlobally(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Retry network and server trouble, never a 4xx: the answer will not change.
      retry: (count, error) => {
        if (isApiError(error) && error.status > 0 && error.status < 500) return false;
        return count < 2;
      },
    },
    mutations: { retry: false },
  },
});
